import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { animeList, getAnimeById } from "@/mocks/anime";

export default function Detail() {
  const { id } = useParams();
  const anime = getAnimeById(Number(id));
  const [faved, setFaved] = useState(false);
  const [toast, setToast] = useState("");

  const related = useMemo(
    () => (anime ? anime.related.map((rid) => getAnimeById(rid)).filter(Boolean) : []),
    [anime]
  );

  const episodeCount = useMemo(() => {
    if (!anime) return 0;
    if (anime.category === "剧场版") return 1;
    const m = anime.episodes.match(/(\d+)/);
    return m ? Math.min(Number(m[1]), 48) : 12;
  }, [anime]);

  useEffect(() => {
    if (!anime) return;
    document.title = `${anime.title} 在线观看 - 樱花看番`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `${anime.title}，${anime.tag}${anime.category}，樱花看番${anime.episodes}在线观看，简介：${anime.desc}`
      );
    }
  }, [anime]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <p className="font-heading text-2xl text-foreground-700">番剧不存在或已下架</p>
          <Link
            to="/library"
            className="mt-5 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 hover:bg-primary-400"
          >
            返回番剧库
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* banner */}
        <section className="relative">
          <div className="absolute inset-0">
            <img
              src={anime.banner}
              alt={`${anime.title} 横版海报`}
              title={`${anime.title} 详情背景`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/70 to-background-50" />
          </div>
          <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 pt-24 pb-10 md:pt-28">
            <nav className="mb-6 flex items-center gap-1 text-xs text-foreground-600">
              <Link to="/" className="hover:text-primary-300">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/library" className="hover:text-primary-300">番剧库</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-400">{anime.title}</span>
            </nav>
            <div className="flex flex-col gap-6 md:flex-row md:items-end">
              <div className="w-36 shrink-0 overflow-hidden rounded-xl border border-background-200/60 md:w-44">
                <img
                  src={anime.poster}
                  alt={`${anime.title} 海报`}
                  title={`${anime.title} 详情`}
                  className="aspect-[2/3] w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-500/20 px-2 py-0.5 text-xs text-primary-300">
                    {anime.category}
                  </span>
                  <span className="rounded-md bg-accent-500/20 px-2 py-0.5 text-xs text-accent-300">
                    {anime.tag}
                  </span>
                  <span className="rounded-md bg-background-800/60 px-2 py-0.5 text-xs text-foreground-500">
                    {anime.status}
                  </span>
                </div>
                <h1 className="mt-3 font-heading text-3xl text-foreground-950 md:text-5xl">
                  {anime.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-600">
                  {anime.year} 年 · {anime.episodes} · {anime.views} 次播放
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-sm text-accent-300">
                    <i className="ri-star-fill" />
                    <span className="font-heading text-2xl">{anime.rating}</span>
                    分
                  </span>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => showToast("已开始播放（示例）")}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
                    >
                      <i className="ri-play-fill text-lg" />
                      立即开播
                    </button>
                    <button
                      onClick={() => {
                        setFaved((v) => !v);
                        showToast(faved ? "已取消收藏" : "已加入追番收藏");
                      }}
                      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-6 py-3 text-sm font-medium transition-colors ${
                        faved
                          ? "border-accent-500/50 bg-accent-500/15 text-accent-300"
                          : "border-background-300/40 text-foreground-700 hover:bg-background-800/60"
                      }`}
                    >
                      <i className={faved ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                      {faved ? "已收藏" : "收藏追番"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* body */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 pb-16">
          <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
            <div className="min-w-0">
              {/* story */}
              <div>
                <h2 className="mb-3 flex items-center gap-2 font-heading text-xl text-foreground-950">
                  <i className="ri-book-open-line text-primary-400" />
                  剧情简介
                </h2>
                <p className="text-sm leading-7 text-foreground-700">{anime.desc}</p>
              </div>

              {/* episodes */}
              <div className="mt-10">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl text-foreground-950">
                  <i className="ri-play-list-2-line text-primary-400" />
                  选集列表
                  <span className="text-sm font-normal text-foreground-600">
                    {anime.category === "剧场版"
                      ? "正片 · 高清"
                      : `${anime.episodes} · 高清`}
                  </span>
                </h2>
                <div className="flex flex-wrap gap-2.5">
                  {Array.from({ length: episodeCount }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => showToast(`正在播放第 ${i + 1} 集（示例）`)}
                      className="inline-flex min-w-[52px] items-center justify-center rounded-lg border border-background-200/60 bg-background-100 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-500/50 hover:bg-primary-500/10 hover:text-primary-300"
                    >
                      {anime.category === "剧场版" ? "正片" : `第${i + 1}集`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* sidebar */}
            <aside className="space-y-6">
              <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                <h3 className="mb-4 font-heading text-lg text-foreground-950">
                  番剧信息
                </h3>
                <ul className="space-y-3 text-sm">
                  {[
                    ["类型", anime.category],
                    ["题材", anime.tag],
                    ["上映年份", String(anime.year)],
                    ["集数", anime.episodes],
                    ["更新状态", anime.status],
                    ["播放热度", anime.views],
                  ].map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-3">
                      <span className="text-foreground-600">{k}</span>
                      <span className="text-right text-foreground-950">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl bg-accent-500/10 p-5">
                <h3 className="flex items-center gap-2 font-heading text-lg text-accent-300">
                  <i className="ri-video-add-line" />
                  追番小贴士
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  收藏后新一集更新会在首页推荐中优先展示，帮你第一时间追更，不错过任何一集。
                </p>
              </div>
            </aside>
          </div>

          {/* related */}
          {related.length > 0 && (
            <div className="mt-14">
              <h2 className="mb-5 font-heading text-xl text-foreground-950">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {related.map((a) => a && <AnimeCard key={a.id} anime={a} />)}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />

      {/* toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}